// client/src/pages/GalleryImagesAdmin.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/GalleryImagesAdmin.css';

// Icônes SVG inline
const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconDelete = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const IconStar = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const IconEye = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const IconUpload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const GalleryImagesAdmin = () => {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback
  const defaultImage = '/images/gallery-default.png';
  
  // Formulaire d'upload
  const [uploadForm, setUploadForm] = useState({
    titre: '',
    description: '',
    ordre: 0,
    actif: true,
    image: null
  });
  
  // Formulaire d'édition
  const [editForm, setEditForm] = useState({
    titre: '',
    description: '',
    ordre: 0,
    actif: true
  });
  
  // Charger la galerie et ses images
  useEffect(() => {
    const fetchGalleryAndImages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/gallery/admin/images/${galleryId}`);
        setGallery(response.data.gallery);
        setImages(response.data.images);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des images:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };
    
    fetchGalleryAndImages();
  }, [galleryId]);
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (imageId) => {
    console.log(`Erreur de chargement d'image ${imageId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [imageId]: true
    }));
  };
  
  // Obtenir l'URL de l'image thumbnail via l'API
  const getThumbnailUrl = (miniature, imageId) => {
    if (!miniature || imageErrors[imageId]) return defaultImage;
    
    // Utiliser la nouvelle route API avec un timestamp pour éviter le cache
    const timestamp = new Date().getTime();
    return `/api/gallery/thumbnail/${miniature}?t=${timestamp}`;
  };
  
  // Ouvrir le modal d'upload
  const openUploadModal = () => {
    setUploadForm({
      titre: '',
      description: '',
      ordre: 0,
      actif: true,
      image: null
    });
    setShowUploadModal(true);
  };
  
  // Ouvrir le modal d'édition
  const openEditModal = (image) => {
    setCurrentImage(image);
    setEditForm({
      titre: image.titre,
      description: image.description || '',
      ordre: image.ordre || 0,
      actif: image.actif
    });
    setShowEditModal(true);
  };
  
  // Gérer le changement du fichier
  const handleFileChange = (e) => {
    setUploadForm({
      ...uploadForm,
      image: e.target.files[0]
    });
  };
  
  // Gérer les changements dans le formulaire d'upload
  const handleUploadFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUploadForm({
      ...uploadForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Gérer les changements dans le formulaire d'édition
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm({
      ...editForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Soumettre le formulaire d'upload
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.image) {
      setError('Veuillez sélectionner une image');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('image', uploadForm.image);
      formData.append('galleryId', galleryId);
      formData.append('titre', uploadForm.titre || uploadForm.image.name);
      formData.append('description', uploadForm.description);
      formData.append('ordre', uploadForm.ordre);
      formData.append('actif', uploadForm.actif);
      
      const response = await axios.post('/api/gallery/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      // Ajouter la nouvelle image à la liste
      setImages([response.data.image, ...images]);
      
      setSuccessMessage('Image uploadée avec succès');
      setShowUploadModal(false);
      setIsUploading(false);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de l\'upload de l\'image:', err);
      setError(`Erreur: ${err.response?.data?.message || err.message}`);
      setIsUploading(false);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Soumettre le formulaire d'édition
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.put(`/api/gallery/image/${currentImage._id}`, editForm);
      
      // Mettre à jour l'image dans la liste
      setImages(images.map(image => 
        image._id === currentImage._id ? response.data : image
      ));
      
      setSuccessMessage('Image mise à jour avec succès');
      setShowEditModal(false);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'image:', err);
      setError(`Erreur: ${err.response?.data?.message || err.message}`);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Supprimer une image
  const handleDeleteImage = async (id, titre) => {
    // Demander confirmation
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'image "${titre}" ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/gallery/image/${id}`);
      
      // Mettre à jour la liste
      setImages(images.filter(image => image._id !== id));
      
      setSuccessMessage('Image supprimée avec succès');
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'image:', err);
      setError(`Erreur: ${err.response?.data?.message || err.message}`);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Définir une image comme couverture de galerie
  const setAsCover = async (imageId) => {
    try {
      const response = await axios.put(`/api/gallery/set-cover/${imageId}`);
      
      // Mettre à jour les données de la galerie
      setGallery(response.data.gallery);
      
      setSuccessMessage('Image définie comme couverture de la galerie');
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la définition de la couverture:', err);
      setError(`Erreur: ${err.response?.data?.message || err.message}`);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  if (loading) return <div className="loading">Chargement de la galerie...</div>;
  
  return (
    <div className="gallery-images-admin-page">
      <div className="gallery-images-admin-header">
        <div className="header-left">
          <Link to="/admin/gallery" className="back-link">
            &laquo; Retour aux galeries
          </Link>
          <h1>Images de la galerie : {gallery?.titre}</h1>
        </div>
        <button className="upload-image-button" onClick={openUploadModal}>
          <IconUpload /> Ajouter des images
        </button>
      </div>
	        
      {/* Messages */}
      {successMessage && <div className="message success">{successMessage}</div>}
      {error && <div className="message error">{error}</div>}
      
      {/* Contenu principal */}
      {images.length === 0 ? (
        <div className="no-images">
          <p>Aucune image dans cette galerie</p>
          <button className="upload-first-image" onClick={openUploadModal}>
            Ajouter votre première image
          </button>
        </div>
      ) : (
        <div className="images-grid">
          {images.map(image => (
            <div key={image._id} className={`image-card ${!image.actif ? 'inactive' : ''}`}>
              <div className="image-card-header">
                <span className="image-order">Ordre: {image.ordre}</span>
                {!image.actif && <span className="inactive-badge">Inactive</span>}
                {gallery.couverture === image.miniature && (
                  <span className="cover-badge" title="Image de couverture">
                    <IconStar />
                  </span>
                )}
              </div>
              <div className="image-thumbnail">
                <img 
                  src={getThumbnailUrl(image.miniature, image._id)}
                  alt={image.titre}
                  onError={() => handleImageError(image._id)}
                />
              </div>
              <div className="image-info">
                <h3 className="image-title">{image.titre}</h3>
                <div className="image-metadata">
                  <p className="image-dimensions">{image.dimensions}</p>
                  <p className="image-size">{image.taille}</p>
                  <p className="image-downloads">{image.downloads} téléchargements</p>
                </div>
              </div>
              <div className="image-actions">
                <button 
                  className="view-button"
                  onClick={() => window.open(`/uploads/gallery/${image.filename}`, '_blank')}
                  title="Voir l'image"
                >
                  <IconEye />
                </button>
                <button 
                  className="set-cover-button"
                  onClick={() => setAsCover(image._id)}
                  title="Définir comme couverture"
                  disabled={gallery.couverture === image.miniature}
                >
                  <IconStar />
                </button>
                <button 
                  className="edit-button"
                  onClick={() => openEditModal(image)}
                  title="Modifier l'image"
                >
                  <IconEdit />
                </button>
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteImage(image._id, image.titre)}
                  title="Supprimer l'image"
                >
                  <IconDelete />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modal d'upload */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content upload-modal">
            <h2>Ajouter une image</h2>
            <form onSubmit={handleUploadSubmit} className="upload-form">
              <div className="form-group file-upload-group">
                <label>Sélectionner une image</label>
                <input 
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                />
                <p className="form-hint">Formats supportés: JPG, PNG, GIF, WebP. Taille max: 10 Mo</p>
              </div>
              
              <div className="form-group">
                <label>Titre de l'image</label>
                <input 
                  type="text"
                  name="titre"
                  value={uploadForm.titre}
                  onChange={handleUploadFormChange}
                  placeholder="Sera rempli avec le nom du fichier si laissé vide"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  name="description"
                  value={uploadForm.description}
                  onChange={handleUploadFormChange}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Ordre d'affichage</label>
                <input 
                  type="number"
                  name="ordre"
                  value={uploadForm.ordre}
                  onChange={handleUploadFormChange}
                  min="0"
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox"
                    name="actif"
                    checked={uploadForm.actif}
                    onChange={handleUploadFormChange}
                  />
                  Image active
                </label>
              </div>
              
              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p>{uploadProgress}% Uploadé</p>
                </div>
              )}
              
              <div className="modal-actions">
                <button 
                  type="submit" 
                  className="save-button"
                  disabled={isUploading}
                >
                  {isUploading ? 'Upload en cours...' : 'Uploader l\'image'}
                </button>
                <button 
                  type="button" 
                  className="cancel-button" 
                  onClick={() => setShowUploadModal(false)}
                  disabled={isUploading}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal d'édition */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content edit-modal">
            <h2>Modifier l'image</h2>
            <form onSubmit={handleEditSubmit} className="edit-form">
              <div className="image-preview">
                <img 
                  src={getThumbnailUrl(currentImage.miniature, currentImage._id)}
                  alt={currentImage.titre}
                />
              </div>
              
              <div className="form-group">
                <label>Titre de l'image</label>
                <input 
                  type="text"
                  name="titre"
                  value={editForm.titre}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  name="description"
                  value={editForm.description}
                  onChange={handleEditFormChange}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Ordre d'affichage</label>
                <input 
                  type="number"
                  name="ordre"
                  value={editForm.ordre}
                  onChange={handleEditFormChange}
                  min="0"
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox"
                    name="actif"
                    checked={editForm.actif}
                    onChange={handleEditFormChange}
                  />
                  Image active
                </label>
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="save-button">
                  Mettre à jour
                </button>
                <button 
                  type="button" 
                  className="cancel-button" 
                  onClick={() => setShowEditModal(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryImagesAdmin;